<template>
  <div :class="COMPONENT_CLASS_NAME" :qa-locator-id="locators.set()">
    <div class="inner" :class="innerClasses">
      <label class="label" :for="id" :qa-locator-id="locators.set('label')">{{ label }}</label>
      <slot />
      <textarea
        :id="id"
        ref="inputRef"
        class="textarea-input"
        :class="textareaClasses"
        :value="modelValue"
        :disabled="disabled"
        :rows="rows"
        :maxlength="maxLength"
        :placeholder="placeholder"
        :qa-locator-id="locators.set('textarea')"
        @input="onInputValue($event)"
        @change="onChangeValue($event)"
        @focus="onFocus"
        @blur="onBlur"
      />
      <button
        v-if="clearable && modelValue && !disabled"
        type="button"
        class="clear"
        aria-label="Очистить поле"
        :qa-locator-id="locators.set('clear')"
        @mousedown.prevent
        @click="onClear"
      >
        <Icon icon="close" size="s" />
      </button>
    </div>
    <div
      class="error-label"
      :class="errorLabelClasses"
      :qa-locator-id="locators.set('error-label')"
    >
      {{ error }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { useQaLocators } from '~/composables/use-qa-locators';

import Icon from '~/components/components/icon/Icon.vue';

import { uuid } from '~/utils/helpers/uuid';

const COMPONENT_CLASS_NAME: string = 'textarea';

const locators = useQaLocators(COMPONENT_CLASS_NAME);

const emit = defineEmits(['update:modelValue', 'input', 'change', 'focus', 'blur', 'clear']);

const modelValue = defineModel({
  type: String,
  default: '',
});

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  rows: {
    type: [String, Number],
    default: 3,
  },
  noResize: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: Number,
    default: null,
  },
  noPaddingBottom: {
    type: Boolean,
    default: false,
  },
  theme: {
    type: String,
    validator: (value: string) => ['gray', 'white', 'blue'].includes(value),
    default: 'gray',
  },
});

defineExpose({
  focus: focusInput,
});

const inputRef = ref<HTMLTextAreaElement | null>(null);
const id: Ref<string> = ref(`textarea-${uuid()}`);
const focused: Ref<boolean> = ref(false);

const innerClasses = computed((): object => {
  return {
    focused: focused.value,
    disabled: props.disabled,
    invalid: props.error,
    active: focused.value || modelValue.value.length,
    [`theme-${props.theme}`]: true,
  };
});

const textareaClasses = computed((): object => {
  return {
    'no-resize': props.noResize,
  };
});

const errorLabelClasses = computed((): object => {
  return {
    show: props.error,
  };
});

function onInputValue(event: Event | InputEvent): void {
  if (props.disabled || !event.target) {
    return;
  }
  const target = event.target as HTMLInputElement;
  emit('input', target.value);
  emit('update:modelValue', target.value);
}

function onChangeValue(event: Event | InputEvent): void {
  if (props.disabled || !event.target) {
    return;
  }
  const target = event.target as HTMLInputElement;
  emit('change', target.value);
}

function focusInput() {
  inputRef.value?.focus();
}

function onClear(): void {
  modelValue.value = '';
  inputRef.value?.blur();
  emit('input', '');
  emit('change', '');
  emit('clear');
}

function onFocus(): void {
  focused.value = true;
  emit('focus');
}

function onBlur(): void {
  focused.value = false;
  emit('blur');
}
</script>

<style lang="scss" scoped>
.textarea {
  > .inner {
    position: relative;
    @include radius_m_sm;
    padding: 12px 32px 12px 16px;
    border: 1px solid;

    &:not(.disabled):not(.invalid):hover,
    &:not(.disabled):not(.invalid).active,
    &.focused {
      border: 1px solid #b0b1b2;

      > .label {
        font-size: 12px;
        line-height: 18px;
      }
    }
    &.invalid {
      border: 1px solid #f23030;
    }
    &.disabled {
      background: #f5f7f9;
    }

    &.theme {
      &-gray {
        background: $new-color-gray-light;
        border: 1px solid $new-color-gray-light;
        &.focused {
          background: #fff;
          border: 1px solid $color-potato-20;
        }
      }
      &-white {
        background: #fff;
        border: 1px solid #dedfdf;
        &.focused {
          background: #fff;
          border: 1px solid $color-potato-20;
        }
      }
      &-blue {
        background: $color-info-secondary;
        border: 1px solid $color-info-secondary;
        &.focused {
          background: #fff;
          border: 1px solid $color-potato-20;
        }
      }
    }
  }

  > .inner > .label {
    color: $color-potato-50;
    display: block;
    cursor: text;
    transition: $transition all;
  }

  > .inner > .textarea-input {
    font-family: $main-font;
    font-size: 14px;
    line-height: 22px;
    border: none;
    outline: none;
    width: 100%;
    padding: 0;

    &.no-resize {
      resize: none;
    }
  }

  > .inner > .clear {
    position: absolute;
    top: 12px;
    right: 12px;
    display: grid;
    width: 24px;
    height: 24px;
    padding: 0;
    place-content: center;
    color: $color-potato-30;
  }

  > .error-label {
    margin-top: 0;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: $color-chilli-50;
    opacity: 0;
    transform: translateY(-10px);
    transition:
      opacity $transition-half,
      transform $transition-half,
      margin $transition-half;
    pointer-events: none;

    &.show {
      opacity: 1;
      transform: translateY(0);
      pointer-events: unset;
      margin-top: 8px;
    }
  }

  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: #f0f0f0;
  }

  ::-webkit-scrollbar-thumb {
    cursor: pointer;
    background: $brand-default-color;
    border-radius: 14px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: $brand-hover-color;
  }
}
</style>
